/**
 * What somebody who has just been given a seat opens — the one page in this
 * area about the **result** rather than the setting.
 *
 * Nine screens show an administrator deciding things. None of them showed what
 * a member actually gets, so a newcomer's first page was a personal dashboard
 * offering to sell them a licence they already hold through their seat. This is
 * the page instead: three numbered acts, and they are the ones `cloud-join.ts`
 * really asks for.
 *
 * **Everything here is computed, and that is the point.** An earlier draft of
 * this screen promised "3 workspaces already cloned, on first launch, with
 * nothing to configure". The code says otherwise and says it plainly:
 * `join(ctx, { orgId, projectId, rootDir })` takes **one** project and **one**
 * folder, so nothing is cloned at sign-in and workspaces are joined one at a
 * time. A welcome screen that promises what the product does not do is worse
 * than no welcome screen, because it is discovered on the one day somebody has
 * no idea which half to disbelieve.
 *
 * So:
 *
 * - the list of workspaces is the **same read** the Workspaces page makes, from
 *   the same grants, so the two cannot disagree about what one person can reach
 *   — and it is empty for somebody who has nothing yet;
 * - what an organization hands a new install is named from its **own**
 *   `onboarding`, key by key, never as "everything Acme decided";
 * - and the third act is the list of refusals the code makes **on purpose** —
 *   `parseOrgSettings` rejects any value that `looksSecret` ("Set it on each
 *   machine instead — an organization's settings are read by every member"), so
 *   Jira's *host* travels and its credential never does.
 *
 * **"Jira, connected" is the claim to avoid.** The board arrives wired to the
 * workspace; the credential does not, and that row belongs in the third list,
 * in amber.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color } from '../../theme/tokens';
import { Note } from '../../components/list';
import { Callout, GhostButton, PrimaryButton } from '../../components/primitives';
import { HairList, HairRow } from '../../components/Section';
import { SkeletonPage, useLoadPhase } from '../../components/Loading';
import { api, apiPath } from '../../lib/api';
import { OTHER_SYSTEMS, platform } from '../../lib/platform';
import { Arrives, sentenceList, Step } from './parts';
import { Settled } from './Settled';
import type { PageId } from '../../components/nav';
import type {
  DeviceTokenRow,
  MemberJoins,
  Organization,
  OrgSettingsRecord,
  Seat,
  WorkspaceList,
  WorkspaceSummary
} from '../../lib/types';

/**
 * The one build there is since 2026-10-06, when the owner stopped Windows and
 * Linux; somebody on either is told so beside the button (`OTHER_SYSTEMS`).
 */
const DOWNLOAD = { label: 'Download for macOS', href: '/api/download?os=mac' } as const;

const AGENT_NAME: Record<string, string> = { 'claude-code': 'Claude Code', codex: 'Codex' };

type Values = Record<string, unknown>;

export function FirstDay({
  organization,
  onGo,
  onSettled
}: {
  organization: Organization;
  onGo: (page: PageId, section?: string | null) => void;
  /** The header says `Welcome to Acme` over the acts and names the page after
   *  them, so the one place that knows which is being drawn hands it up. */
  onSettled: (settled: boolean | null) => void;
}): React.JSX.Element {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(null);
  const [onboarding, setOnboarding] = useState<Values | null>(null);
  const [admins, setAdmins] = useState<Seat[]>([]);
  const [linked, setLinked] = useState<number | null>(null);
  const [joins, setJoins] = useState<MemberJoins['joins'] | null>(null);
  const [system] = useState(platform);

  useEffect(() => {
    let live = true;
    // Five independent reads, each allowed to fail on its own. Nothing here is
    // a precondition for anything else: a page that showed nothing because the
    // seat list would not load would be a welcome screen withheld over a
    // footnote.
    void api
      .get<WorkspaceList>(apiPath`/orgs/${organization.id}/workspaces`)
      .then((answer) => live && setWorkspaces(answer.workspaces.filter((one) => !one.archivedAt)))
      .catch(() => live && setWorkspaces([]));
    void api
      .get<OrgSettingsRecord>(apiPath`/orgs/${organization.id}/settings`)
      .then((answer) => live && setOnboarding(answer.settings.onboarding as Values))
      .catch(() => live && setOnboarding({}));
    void api
      .get<{ seats: Seat[] }>(apiPath`/orgs/${organization.id}/seats`)
      .then((answer) => live && setAdmins(answer.seats.filter((seat) => seat.role === 'owner' || seat.role === 'admin')))
      .catch(() => undefined);
    // A *linked* machine is one holding a device token that has not been
    // revoked, which is what act one asks for — an install of the app alone
    // pulls nothing, and a machine that was unlinked is one of these rows with
    // a `revokedAt`, not a machine.
    void api
      .get<{ tokens?: DeviceTokenRow[] }>('/devices')
      .then((answer) => live && setLinked((answer.tokens ?? []).filter((token) => !token.revokedAt).length))
      .catch(() => live && setLinked(0));
    // The two facts that decide which of the two pages this is. A read that
    // fails answers **zero**, never "not yet": the page it picks then is the
    // one that says what to do, and a member who has done it already loses
    // nothing but a screen they have read.
    void api
      .get<MemberJoins>(apiPath`/orgs/${organization.id}/overview/mine`)
      .then((answer) => live && setJoins(answer.joins))
      .catch(() => live && setJoins([]));
    return () => {
      live = false;
    };
  }, [organization.id]);

  const on = onboarding ?? {};
  const agents = Array.isArray(on.agents) ? (on.agents as string[]) : [];
  const jira = Boolean(on.jiraEnabled) && typeof on.jiraHost === 'string' && on.jiraHost.length > 0;
  const trello = Boolean(on.trelloEnabled);
  const installed = linked !== null && linked > 0;

  /* ── the day after ────────────────────────────────────────────────────── */

  // The acts are read once. A machine of theirs is linked and a workspace of
  // the organization's is joined, and the page that says "Install Airlock" is
  // a numbered instruction sitting over what somebody came to look at — so
  // from that moment this draws the settled page instead.
  //
  // Both halves are read off what happened rather than remembered in this
  // browser: a token that has not been revoked, and a `project.join` in the
  // organization's own trail. The switch follows the person to their next
  // machine and their next browser, and goes back on its own for somebody who
  // unlinks everything — which is the state they would be in.
  //
  // A join counts only where the workspace is still one they can reach:
  // somebody taken off the team that shared it has nowhere to work again, and
  // the page that says so is the first one.
  const answered = linked !== null && joins !== null && workspaces !== null;
  const joinedHere = (joins ?? []).filter((one) => (workspaces ?? []).some((workspace) => workspace.id === one.projectId));
  const settled = installed && joinedHere.length > 0;

  const phase = useLoadPhase(!answered);

  useEffect(() => {
    onSettled(answered ? settled : null);
    return () => onSettled(null);
  }, [answered, settled, onSettled]);

  /* ── 2 · what one join brings, and what it hands the machine once ─────── */

  const arriving: [string, string][] = [
    ['The repositories, on the right branch', 'With the commands that install them, run them and check them.'],
    ['The settings', `${organization.name}’s and the workspace’s own. Its workflows and its API console come along.`],
    ...(jira || trello
      ? ([
          [
            'Its ticket board',
            `Already wired to the workspace. Signing in to ${jira ? 'Jira' : 'Trello'} is still yours to do.`
          ]
        ] as [string, string][])
      : []),
    ['The secrets', 'Injected when a session launches, never shown. If one is missing, the join says so on arrival.']
  ];

  // Named key by key from this organization's own `onboarding`, because the
  // six that travel are a short list and "everything Acme decided" is not one
  // of them.
  const handed: string[] = [
    ...(agents.length ? [sentenceList(agents.map((agent) => AGENT_NAME[agent] ?? agent))] : []),
    ...(on.agentSettings ? ['how each one is configured'] : []),
    ...(jira ? [`the Jira site ${String(on.jiraHost)}`] : []),
    ...(trello ? ['Trello'] : []),
    ...(typeof on.taskSyncMinutes === 'number' ? [`a ticket check every ${on.taskSyncMinutes} minutes`] : []),
    ...(on.bookmarkletEnabled === false ? ['no bookmarklet'] : [])
  ];

  /* ── 3 · what no organization can pre-fill ────────────────────────────── */

  const yourself: { what: string; why: string; warn?: boolean }[] = [
    {
      what: 'Install git',
      why: 'Without git nothing clones. The app says so rather than failing quietly, and keeps the retry.'
    },
    ...(on.gitEnabled === false
      ? []
      : [
          {
            what: 'Sign in to GitHub',
            why: `${organization.name}’s private repositories need it. A repository that fails keeps its row, and you retry it afterwards.`
          }
        ]),
    ...(jira || trello
      ? [
          {
            what: `Sign in to ${jira ? 'Jira' : 'Trello'}`,
            why: `${organization.name} gives you the address, never the token. Every member reads the same settings document, so no credential is written in it.`,
            warn: true
          }
        ]
      : []),
    {
      what: `Sign in to ${agents.length ? sentenceList(agents.map((agent) => AGENT_NAME[agent] ?? agent)) : 'your agents'}`,
      why: 'Those subscriptions are yours. The token stays on your computer and never enters a micro-VM.'
    }
  ];

  // Held until all three reads are answered. The two pages are different
  // enough that guessing for 200ms and correcting is a flicker on the one
  // screen whose job is to say where somebody stands.
  if (!answered) return <div style={{ padding: '22px var(--al-gutter) 30px' }}>{phase === 'skeleton' && <SkeletonPage />}</div>;
  if (settled) {
    return (
      <Settled
        organization={organization}
        workspaces={workspaces ?? []}
        joinedAt={new Map(joinedHere.map((one) => [one.projectId, one.at]))}
        linked={linked ?? 0}
        handed={handed}
        onGo={onGo}
      />
    );
  }

  // Somebody can be here without a seat now: an invitation costs nothing, so a
  // colleague joins on the day they are hired and is given a seat when one is
  // free. Act one would otherwise send them to install an app that will not
  // unlock and link a machine the Worker refuses — a first day spent finding
  // out that the welcome screen was written for somebody else.
  const seated = organization.seatStatus !== 'member';
  // Either one of them is enough, so « or », and the verb agrees (52a).
  const givers = admins.length ? sentenceList(admins.slice(0, 2).map((seat) => seat.email), 'or') : 'an administrator';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32, padding: '22px var(--al-gutter) 30px' }}>
      {!seated && (
        <Callout tone="amber" title={`You are in ${organization.name}, and no seat has been given to you yet`}>
          A seat is what carries the licence, so Airlock will not unlock and this computer cannot be linked until{' '}
          {givers} gives you
          one. Everything below is what happens the moment they do — your teams and the workspaces shared with you are
          already waiting.
        </Callout>
      )}
      {installed ? (
        // A done step folds to its header: the title in the past, its meta
        // and one action (§40.5).
        <Step
          n={1}
          of={3}
          state="done"
          title="Airlock is linked on this computer"
          meta={`${linked} linked ${linked === 1 ? 'machine' : 'machines'}`}
          actions={
            <GhostButton size="sm" onClick={() => onGo('machines')}>
              Machines
            </GhostButton>
          }
        />
      ) : (
        <Step
          n={1}
          of={3}
          state={seated ? 'current' : 'todo'}
          title="Install Airlock and link this computer"
          lede={
            <>
              {seated
                ? 'Your seat is given: Airlock links itself to this account the first time it opens, with nothing to type.'
                : 'Worth installing now; it unlocks by itself on the day your seat is given, with nothing to type.'}
              {system !== 'mac' && (
                <>
                  {' '}
                  {OTHER_SYSTEMS}
                </>
              )}
            </>
          }
          actions={
            <PrimaryButton size="sm" tone="ink" href={DOWNLOAD.href}>
              {DOWNLOAD.label}
            </PrimaryButton>
          }
        />
      )}

      <Step
        n={2}
        of={3}
        state={installed ? 'current' : 'todo'}
        title="Join your workspaces"
        lede="In the app, one at a time. You pick the folder; Airlock does the rest."
      >
        {(workspaces ?? []).length === 0 ? (
          <Note>
            Nothing is shared with you yet. A workspace appears here the moment somebody in {organization.name} publishes
            one and gives your team access — there is nothing for you to ask for.
          </Note>
        ) : (
          <HairList label="Your workspaces">
            {(workspaces ?? []).map((workspace) => (
              <HairRow
                key={workspace.id}
                mark={{ dot: workspace.color }}
                title={workspace.name}
                description={`${workspace.projects} ${workspace.projects === 1 ? 'repository' : 'repositories'}`}
                end={`${workspace.access.right} · ${workspace.access.via}`}
              />
            ))}
          </HairList>
        )}

        <Arrives items={arriving} />

        <Note>
          {handed.length ? (
            <>
              <span style={{ color: color.ink }}>On the first one</span>, {organization.name} also sets this computer up:{' '}
              {sentenceList(handed)}. It happens once — after that these are yours to change.
            </>
          ) : (
            <>
              {organization.name} hands a new install no settings of its own, so yours stay yours from the first launch.
              What it shares is the workspaces above, and what each of them carries.
            </>
          )}
        </Note>
      </Step>

      <Step
        n={3}
        of={3}
        state="todo"
        title="Sign in, once to each"
        lede={`These accounts are yours. ${organization.name} cannot fill them in for you, and that is on purpose.`}
      >
        {/* Things to do oneself, which Airlock cannot tick: a ring, not a
            checkbox (§40.3); a refusal keeps its amber sentence. */}
        <HairList label="Accounts to sign in to">
          {yourself.map((one) => (
            <HairRow
              key={one.what}
              mark="ring"
              title={one.what}
              description={one.warn ? <span style={{ color: color.warnText }}>{one.why}</span> : one.why}
            />
          ))}
        </HairList>
      </Step>

      {admins.length > 0 && (
        <Note>
          A question about a setting? Ask{' '}
          {sentenceList(admins.slice(0, 2).map((seat) => seat.email))}
          {admins.length > 2 ? ` or one of the ${admins.length - 2} others` : ''}, who{' '}
          {admins.length === 1 ? 'administers' : 'administer'} {organization.name}.
        </Note>
      )}
    </div>
  );
}
